import { useRef } from "react";
import { Link } from "react-router-dom";
import FadeIn from "../../../components/base/FadeIn";

const animals = [
  {
    name: "Ara Papağanı",
    latin: "Ara macao",
    tag: "En Popüler",
    desc: "Muhteşem renkleri ve yüksek zekasıyla en çok tercih edilen papağan türü.",
    image: "/assets/images/sc4-macaw.jpg",
    color: "from-primary-900/70",
  },
  {
    name: "Jako Papağanı",
    latin: "Psittacus erithacus",
    tag: "Özel Bakım",
    desc: "Dünyanın en zeki kuş türlerinden biri. Konuşma yeteneğiyle büyüleyici.",
    image: "/assets/images/sc4-jako.jpg",
    color: "from-gray-800/70",
  },
  {
    name: "Leopar Gecko",
    latin: "Eublepharis macularius",
    tag: "Egzotik",
    desc: "Renkli desenleri ve sakin yapısıyla en sevilen sürüngen türlerinden.",
    image: "/assets/images/sc4-gecko.jpg",
    color: "from-amber-800/70",
  },
  {
    name: "Muhabbet Kuşu",
    latin: "Melopsittacus undulatus",
    tag: "",
    desc: "Sevimli kişiliği ve konuşma yeteneğiyle en popüler süs kuşu.",
    image: "/assets/images/sc4-budgie-v2.jpg",
    color: "from-teal-800/70",
  },
  {
    name: "Hamster",
    latin: "Mesocricetus auratus",
    tag: "Sevimli",
    desc: "Küçük bedeni ve büyük kişiliğiyle ailelerin gözdesi.",
    image: "/assets/images/sc4-hamster.jpg",
    color: "from-orange-800/70",
  },
  {
    name: "Su Kaplumbağası",
    latin: "Trachemys scripta",
    tag: "",
    desc: "Uzun ömürlü ve bakımı keyifli yarı sucul sürüngen.",
    image: "/assets/images/sc4-turtle.jpg",
    color: "from-emerald-800/70",
  },
];

export default function ExoticShowcase() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "right" ? 340 : -340, behavior: "smooth" });
  };

  return (
    <section className="py-20 md:py-28 bg-[#f5f3ef] overflow-hidden">
      <div className="w-full">

        {/* Header */}
        <div className="px-4 md:px-8 lg:px-12 mb-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <FadeIn>
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-pulse" />
                  Hizmet Alanımız
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Baktığımız{" "}
                  <span className="font-serif italic text-primary-700">Dostlar</span>
                </h2>
                <p className="text-gray-500 mt-3 text-base max-w-md">
                  50'den fazla egzotik türe özel teşhis, tedavi ve cerrahi hizmetleri.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.1}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => scroll("left")}
                  className="w-11 h-11 rounded-full bg-white hover:bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 transition-all duration-200 cursor-pointer shadow-sm"
                >
                  <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center" />
                </button>
                <button
                  onClick={() => scroll("right")}
                  className="w-11 h-11 rounded-full bg-white hover:bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 transition-all duration-200 cursor-pointer shadow-sm"
                >
                  <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
                </button>
                <Link
                  to="/kus-egzotik"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-all duration-300 whitespace-nowrap ml-2"
                >
                  Tümünü Gör
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* Horizontal Scroll Cards */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide px-4 md:px-8 lg:px-12 pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {animals.map((animal, index) => (
            <FadeIn key={animal.name} delay={index * 0.08}>
              <div className="relative flex-shrink-0 w-[280px] md:w-[320px] h-[440px] md:h-[500px] rounded-3xl overflow-hidden group cursor-pointer">
                {/* Image */}
                <img
                  src={animal.image}
                  alt={animal.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  loading="eager"
                />

                {/* Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-t ${animal.color} via-transparent to-transparent`} />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/30 to-transparent" />

                {/* Tag */}
                {animal.tag && (
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 bg-primary-500 text-white text-xs font-bold rounded-full">
                      {animal.tag}
                    </span>
                  </div>
                )}

                {/* Number */}
                <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                  <span className="text-gray-600 text-xs font-bold">0{index + 1}</span>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white/60 text-xs italic mb-1">{animal.latin}</p>
                  <h3 className="text-white font-bold text-xl mb-2">{animal.name}</h3>
                  <p
                    className="text-white/60 text-xs leading-relaxed overflow-hidden transition-all duration-500"
                    style={{ maxHeight: "0px", opacity: 0 }}
                  >
                    {animal.desc}
                  </p>

                  {/* Hover reveal */}
                  <div className="mt-3 flex items-center gap-2 text-primary-300 text-xs font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                    <i className="ri-stethoscope-line w-4 h-4 flex items-center justify-center" />
                    Özel veteriner bakımı
                  </div>
                </div>

                {/* Hover border glow */}
                <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary-400/60 transition-all duration-500" />
              </div>
            </FadeIn>
          ))}

          {/* Last card — CTA */}
          <FadeIn delay={0.5}>
            <div className="flex-shrink-0 w-[280px] md:w-[320px] h-[440px] md:h-[500px] rounded-3xl overflow-hidden bg-white border border-gray-200 flex flex-col items-center justify-center gap-6 p-8 text-center shadow-sm">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className="ri-add-line text-3xl text-primary-600 w-8 h-8 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-gray-900 font-bold text-xl mb-2">50+ Tür Daha</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Tüm egzotik türleri ve bakım bilgilerini keşfedin.
                </p>
              </div>
              <Link
                to="/kus-egzotik"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-all duration-300 whitespace-nowrap text-sm"
              >
                Tümünü Keşfet
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </FadeIn>
        </div>

        {/* Bottom stats */}
        <div className="px-4 md:px-8 lg:px-12 mt-10">
          <div className="max-w-7xl mx-auto">
            <FadeIn delay={0.3}>
              <div className="grid grid-cols-3 gap-4 max-w-lg">
                {[
                  { num: "50+", label: "Egzotik Tür" },
                  { num: "2020", label: "Kuruluş Yılı" },
                  { num: "5000+", label: "Mutlu Hasta" },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-2xl md:text-3xl font-bold text-primary-700">{s.num}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>

      </div>
    </section>
  );
}
